import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SystemWheel from "../components/SystemWheel";
import { pillarName, PILLAR_CARDS, CALENDAR_URL_PRIORITY, CALENDAR_URL_STANDARD } from "../mock";
import { BookingButton } from "../components/BookingButton";
import { WhatYouToldUs } from "../components/results/WhatYouToldUs";
import { SandraVideo } from "../components/results/SandraVideo";
import { DoctorCallout, Guarantee, Testimonials } from "../components/results/ResultsBlocks";
import { Glp1Section } from "../components/results/Glp1Section";
import { HowFixedCards } from "../components/results/HowFixedCards";
import { SuccessStories } from "../components/results/SuccessStories";
import {
  SYMPTOM_COPY,
  IF_NOTHING_CHANGES,
  HORMONE_NOTE,
  WHY_ONE_COACH,
  WHY_ONE_COACH_CLOSING,
  HOW_FIXED_NEW_PARAGRAPH,
  CTA_LABEL,
  CTA_SUBLINE,
} from "../lib/resultsCopy";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF35";

// Calendly's phone question expects an international number; US numbers get +1.
const normalizePhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
};

const RESULTS_DISCLAIMER =
  "The Menopause Matrix\u2122 is an educational self-assessment. It is not a diagnostic tool and does not constitute medical advice or a diagnosis.";

const Eyebrow = ({ children, className = "" }) => (
  <p className={`text-[12px] font-bold tracking-[0.18em] uppercase text-[#D4AF35] ${className}`}>{children}</p>
);

// Chapter marker: thin gold divider, gold number circle, "STEP n" + step name. Left-justified.
const StepMarker = ({ n, name }) => (
  <div className="flex flex-col items-start" data-testid={`step-marker-${n}`}>
    <span className="block w-[120px] h-px" style={{ backgroundColor: "rgba(212,175,53,0.4)" }} aria-hidden="true" />
    <div className="mt-7 inline-flex items-center gap-4 text-left">
      <span
        className="flex items-center justify-center shrink-0 w-11 h-11 md:w-14 md:h-14 rounded-full font-display font-black text-[22px] md:text-[28px] leading-none"
        style={{ backgroundColor: GOLD, color: "#0D0D0D" }}
        aria-hidden="true"
      >
        {n}
      </span>
      <span className="flex flex-col">
        <span className="font-bold text-[13px] uppercase leading-none" style={{ letterSpacing: "3px", color: GOLD }}>
          Step {n} of 3
        </span>
        <span className="mt-1.5 font-display font-black uppercase text-white text-[20px] md:text-[26px] leading-[1.1] tracking-tight">
          {name}
        </span>
      </span>
    </div>
  </div>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-white leading-tight ${className}`}>
    {children}
  </h2>
);

const ResultsPage = () => {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(undefined);
  const [buttonOnScreen, setButtonOnScreen] = useState(false);
  const buttonRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Your Matrix | Sassy Full Stack Fitness";
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/matrix/${resultId}`)
      .then((resp) => {
        if (!active) return;
        setRecord(resp.data);
        track("results_view");
      })
      .catch(() => {
        if (active) setRecord(null);
      });
    return () => {
      active = false;
    };
  }, [resultId]);

  // Sticky bar is fixed for the whole page but steps aside while any booking button is on screen.
  useEffect(() => {
    const els = buttonRefs.current.filter(Boolean);
    if (!els.length) return;
    const visible = new Set();
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
        setButtonOnScreen(visible.size > 0);
      },
      { threshold: 0.25 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [record]);

  if (record === undefined) return <div className="min-h-screen bg-background" />;

  if (record === null) {
    return (
      <div className="bg-background min-h-screen flex flex-col">
        <Header minimal />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-white">
            That Matrix link isn't valid anymore.
          </h1>
          <button
            onClick={() => navigate("/matrix")}
            className="mt-8 inline-flex items-center rounded-full bg-primary text-primary-foreground font-bold px-8 py-4 hover:brightness-110 transition focus-ring"
          >
            Take the Matrix
          </button>
        </div>
        <Footer legalOnly extraDisclaimer={RESULTS_DISCLAIMER} />
      </div>
    );
  }

  const { first_name, active_pillars, active_count, email, lead_tier, checked_items, primary_symptom, hrt_status } = record;
  const startHere = record.start_here_system || (active_pillars || [])[0] || "nutrition";
  const supporting = (record.supporting_systems || []).filter(Boolean);
  const startHereName = pillarName(startHere);
  const concern = SYMPTOM_COPY[primary_symptom];
  const hormonalActive = (active_pillars || []).includes("hormonal");

  const calUrl = lead_tier === "A" ? CALENDAR_URL_PRIORITY : CALENDAR_URL_STANDARD;
  const lastName = record.last_name || "";
  const fullName = [first_name, lastName].filter(Boolean).join(" ");
  const phone = normalizePhone(record.phone);
  const bookHref =
    `${calUrl}?name=${encodeURIComponent(fullName)}` +
    `&first_name=${encodeURIComponent(first_name)}` +
    (lastName ? `&last_name=${encodeURIComponent(lastName)}` : "") +
    `&email=${encodeURIComponent(email)}` +
    (phone ? `&a1=${encodeURIComponent(phone)}` : "");

  const headlineText = concern
    ? `${first_name}, ${concern.phrase} ${concern.verb} in your ${startHereName} system.`
    : `${first_name}, your symptoms start in your ${startHereName} system.`;
  const longHeadline = headlineText.length > 75;

  const threeCards = [startHere, ...supporting];
  const cardCount = threeCards.length;
  const cardHeading = cardCount === 1 ? "Focus" : cardCount === 2 ? "Two" : "Three";
  const cardClosing =
    cardCount === 1
      ? "The other systems still matter. Every plan touches all seven, but this is where yours starts."
      : "The other systems still matter. Every plan touches all seven, but these are where yours starts.";

  const renderCta = (slot, index) => (
    <div
      ref={(el) => (buttonRefs.current[index] = el)}
      className="w-[90%] max-w-[760px] mx-auto mt-14 text-center"
      data-testid={`results-cta-${slot}`}
    >
      <BookingButton
        href={bookHref}
        context={{ name: fullName, email, result_id: record.result_id }}
        onClick={() => track("book_call_click", { location: "results", button: slot, lead_tier })}
        label={CTA_LABEL}
        subline={CTA_SUBLINE}
        testid={`booking-cta-${slot}`}
        className="w-full md:w-auto"
      />
    </div>
  );

  const body = "space-y-5 text-lg text-white/75 leading-relaxed font-medium";

  return (
    <div className="bg-background min-h-screen">
      <Header minimal />

      <main className="pt-28 pb-32 md:pb-24">
        {/* 3.2 Hero — STEP 1 */}
        <section className="w-[90%] max-w-[820px] mx-auto text-left" data-testid="results-reveal">
          <StepMarker n={1} name="Your Results" />
          <h1
            className={`mt-6 font-display font-black uppercase leading-[1.08] text-white ${
              longHeadline ? "text-[28px]" : "text-[32px]"
            } md:text-[44px]`}
            style={{ letterSpacing: "0.01em", wordSpacing: "0.12em" }}
            data-testid="results-h1"
          >
            {concern ? (
              <>
                {first_name}, <span style={{ color: GOLD }}>{concern.phrase}</span> {concern.verb} in your {startHereName} system.
              </>
            ) : (
              <>
                {first_name}, your symptoms start in your <span style={{ color: GOLD }}>{startHereName}</span> system.
              </>
            )}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white font-medium leading-relaxed" data-testid="results-subline">
            You told us it bothers you most. So that's exactly where your plan starts.
          </p>
          <p className="mt-3 text-base md:text-lg text-white/75 font-medium" data-testid="results-count">
            Your symptoms are hitting <span className="font-bold" style={{ color: GOLD }}>{active_count}</span> of the 7 systems.
          </p>
        </section>

        {/* 3.3 Wheel (unchanged) */}
        <section className="w-[90%] max-w-[900px] mx-auto mt-16">
          <div className="bg-white/[0.03] border border-white/10 rounded-[2rem] p-8 md:p-12">
            <SystemWheel mode="results" activePillars={active_pillars} startHereId={startHere} />
          </div>
        </section>

        {/* 3.4 What You Told Us */}
        <WhatYouToldUs checkedItems={checked_items} primarySymptom={primary_symptom} startHere={startHere} supporting={supporting} />

        {/* 3.4b Video (moved here from after the Step 3 button, per user) */}
        <SandraVideo />

        {/* 3.5 Booking 1 of 4 */}
        {renderCta("top", 0)}

        {/* 3.6 Why it hasn't held — STEP 2 */}
        <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="step-2">
          <StepMarker n={2} name="What Your Body Is Telling You" />
          <H2 className="mt-6" >
            <span data-testid="start-here-line">
              Start Here: <span style={{ color: GOLD }}>{startHereName}</span>.
            </span>
          </H2>
          <div className={`mt-7 ${body}`}>
            <p className="text-white font-semibold text-xl">You weren't doing it wrong. You were working on one system at a time.</p>
            <p>
              Sleep affects cortisol. Cortisol affects where fat is stored. Muscle loss slows your metabolism, which changes how you
              handle carbohydrates, which changes your energy and your sleep. Pull on one and the others move.
            </p>
            <p className="text-white/90">Which is why fixing one at a time hasn't held.</p>
          </div>
        </section>

        {/* 3.7 If nothing changes */}
        <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="if-nothing-changes">
          <H2>Here's what next year looks like if nothing changes.</H2>
          <div className={`mt-7 ${body}`}>
            {(IF_NOTHING_CHANGES[startHere] || IF_NOTHING_CHANGES.nutrition).map((p, i) => (
              <p key={i} className={i === 0 ? "text-white font-semibold text-xl" : ""}>
                {p}
              </p>
            ))}
            <p className="text-white font-bold">This doesn't fix itself by waiting. It isn't a willpower problem. It's an order problem.</p>
          </div>
          <div className="mt-7 border-l-[3px] pl-5 py-1" style={{ borderColor: GOLD }} data-testid="hard-part-callout">
            <p className="text-lg md:text-xl font-semibold leading-snug" style={{ color: GOLD }}>
              You already did the hard part. You found out where it starts.
            </p>
          </div>
        </section>

        {/* 3.7b GLP-1 (static, every result) */}
        <Glp1Section H2={H2} body={body} />

        {/* 3.8 Your road map — STEP 3 */}
        <section className="w-[90%] max-w-[1000px] mx-auto mt-20" data-testid="step-3">
          <StepMarker n={3} name="Your Road Map" />
          <h2 className="mt-6 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-left text-white">
            Your <span className="text-gold-gradient">{cardHeading}</span>
          </h2>
          <div
            className={
              cardCount === 1
                ? "mt-10 flex justify-center"
                : cardCount === 2
                ? "mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-[720px] mx-auto"
                : "mt-10 grid grid-cols-1 md:grid-cols-3 gap-6"
            }
          >
            {threeCards.map((pid, i) => {
              const card = PILLAR_CARDS[pid];
              const isPrimary = i === 0;
              return (
                <div
                  key={pid}
                  className={`rounded-[1.75rem] p-7 border transition-colors ${cardCount === 1 ? "w-full max-w-[380px]" : ""} ${
                    isPrimary ? "bg-primary/10 border-primary/50 md:scale-[1.03]" : "bg-card/60 border-white/10"
                  }`}
                >
                  <span className={`text-[10px] font-bold tracking-[0.25em] uppercase ${isPrimary ? "text-primary" : "text-white/45"}`}>
                    {isPrimary ? "Start Here" : "Supporting"}
                  </span>
                  <h3 className="mt-2 font-display font-bold uppercase tracking-tight text-xl text-white">{card.heading}</h3>
                  <p className="mt-3 text-white/70 leading-relaxed font-medium text-[15px]">{card.body}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-white/70 leading-relaxed font-medium max-w-2xl mx-auto text-center">{cardClosing}</p>
        </section>

        {/* 3.9 Booking 2 of 4 */}
        {renderCta("middle", 1)}

        {/* 3.10 A note on hormones (only when Hormonal is one of her checked systems) */}
        {hormonalActive && (
          <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="hormonal-block">
            <H2>A Note on Hormones</H2>
            <p className="mt-6 text-lg text-white/75 leading-relaxed font-medium" data-testid="hormonal-note">
              {HORMONE_NOTE[hrt_status] || HORMONE_NOTE.no}
            </p>
          </section>
        )}

        {/* 3.12 Why one coach */}
        <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="why-one-coach">
          <H2>Why One Coach Changes the Answer</H2>
          <div className={`mt-7 ${body}`}>
            {WHY_ONE_COACH.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
          <DoctorCallout />
          <p className="text-lg text-white/75 leading-relaxed font-medium">{WHY_ONE_COACH_CLOSING}</p>
        </section>

        {/* 3.13 How this gets fixed */}
        <section className="w-[90%] max-w-[760px] mx-auto mt-20" data-testid="how-fixed">
          <H2>How This Gets Fixed</H2>
          <div className={`mt-7 ${body}`}>
            <p>
              One health and fitness coach who owns all seven systems: training, nutrition, gut health, cardiovascular activity and
              recovery in-house, plus labs, hormone and metabolic support through our vetted medical, lab and pharmacy partners when
              you're ready.
            </p>
            <p data-testid="how-fixed-leadin">
              One evidence-based, personalized plan built in the order your body needs, monitored and adjusted as you go. Here's what
              Sandra builds for you:
            </p>
          </div>
          <HowFixedCards />
          <div className={`mt-8 ${body}`}>
            <p className="text-white font-semibold" data-testid="how-fixed-coordinated">
              Everything coordinated for you, so nothing falls through the cracks.
            </p>
            <p data-testid="medical-partner-paragraph">{HOW_FIXED_NEW_PARAGRAPH}</p>
            <p className="text-white font-semibold">One dedicated expert partner, with you every step of the way in your transformation.</p>
            <p>
              Every woman moves through all seven. But where you start, how long you stay, and what order they run in depends on your
              stage, your labs, and which system is driving the rest.
            </p>
            <p className="text-white font-semibold text-xl">Your Matrix shows what's happening. The call determines where you start.</p>
          </div>
        </section>

        {/* 3.14 Guarantee */}
        <Guarantee />

        {/* 3.15 Booking 3 of 4 */}
        {renderCta("lower", 2)}

        {/* 3.15b Success stories (user-supplied section) */}
        <SuccessStories />

        {/* 3.16 Testimonials */}
        <Testimonials />

        {/* 3.17 Close, final button, P.S. */}
        <section className="w-[90%] max-w-[760px] mx-auto mt-20 text-center" data-testid="results-close">
          <H2>You Already Did the Hard Part.</H2>
          <p className="mt-5 text-lg text-white/75 leading-relaxed font-medium">
            You were honest about what you're living with. Now let Sandra show you where to start.
          </p>
          <div ref={(el) => (buttonRefs.current[3] = el)} className="mt-8" data-testid="results-cta-bottom">
            <BookingButton
              href={bookHref}
              context={{ name: fullName, email, result_id: record.result_id }}
              onClick={() => track("book_call_click", { location: "results", button: "bottom", lead_tier })}
              label={CTA_LABEL}
              subline={CTA_SUBLINE}
              testid="booking-cta"
              className="w-full md:w-auto"
            />
          </div>
          <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-xl mx-auto">
            Sandra reads your Matrix before you talk. You'll leave knowing which system to start with, and why.
          </p>
          <div className="mt-8 pt-6 border-t border-white/10 text-left">
            <p className="text-base text-white/75 leading-relaxed font-medium" data-testid="results-ps">
              <span className="font-bold" style={{ color: GOLD }}>P.S.</span> You told us what bothers you most:{" "}
              {concern ? concern.short : `your ${startHereName.toLowerCase()} system`}. That's where Sandra will start on your call.
              Thirty minutes, no cost, and you'll leave with your first step, whether or not you ever work with us.
            </p>
          </div>
          <p className="mt-6 text-sm text-white/50">
            Your results are saved. We emailed a link to <span className="text-white/70 font-semibold">{email}</span> so you can come back
            to this page anytime.
          </p>
        </section>
      </main>

      <Footer legalOnly extraDisclaimer={RESULTS_DISCLAIMER} />

      {/* Sticky bar (mobile): fixed for the whole page, steps aside while a booking button is on screen */}
      {!buttonOnScreen && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 py-2.5 bg-background/95 backdrop-blur-xl border-t border-white/10 flex items-center px-4"
          data-testid="results-sticky-bar"
        >
          <BookingButton
            href={bookHref}
            context={{ name: fullName, email, result_id: record.result_id }}
            onClick={() => track("book_call_click", { location: "results", button: "sticky", lead_tier })}
            label={CTA_LABEL}
            subline={null}
            compact
            testid="booking-cta-sticky"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default ResultsPage;
